/**
 * TenantMergeModal — fold a duplicate (source) tenant into the current
 * (survivor) tenant. Variante A: large modal on the survivor's detail page.
 *
 * Flow: pick source (search, survivor excluded client-side) → optional reason →
 * "Vorschau prüfen" (explicit) → review counts/conflicts/warnings → "Zusammenführen".
 *
 * Backend: POST /api/admin/tenants/{survivor_id}/merge-preview (read-only) and
 * .../merge (atomic). Conflicts come back in the preview body (can_merge=false);
 * 404/422/409 throw with a German detail string.
 *
 * Toast responsibility: the SUCCESS toast is fired HERE. The parent's onMerged
 * only refreshes — no second toast.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  executeTenantMerge,
  fetchAdminTenants,
  previewTenantMerge,
} from "../../../api/adminData";
import { tenantDisplayName } from "../../../utils/tenantDisplayName";
import TenantParticipantPicker from "../../../pages/admin/tenant/TenantParticipantPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

const COUNT_LABELS = {
  tenancies: "Mietverhältnisse",
  tenancy_participants: "Mietparteien",
  tenant_addresses: "Adressen",
  tenant_notes: "Notizen",
  tenant_events: "Verlaufseinträge",
  tenant_documents: "Dokumente",
  tasks: "Aufgaben",
  finance_documents: "Finanzdokumente",
  invoices: "Rechnungen",
};

const FIELD_LABELS = {
  first_name: "Vorname",
  last_name: "Nachname",
  birth_date: "Geburtsdatum",
  email: "E-Mail",
  phone: "Telefon",
  company: "Firma",
  nationality: "Nationalität",
  is_swiss: "Schweizer Staatsangehörigkeit",
  residence_permit: "Aufenthaltsbewilligung",
  iban: "IBAN",
  street: "Strasse",
  postal_code: "PLZ",
  city: "Ort",
  country: "Land",
  languages: "Sprachen",
  application_id: "Bewerbung",
};

const labelCls =
  "block text-[12px] font-semibold text-[#94a3b8] mb-1";
const fieldCls =
  "w-full rounded-md border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-sm text-[#e2e8f0] outline-none focus:border-[#5b8cff]";

function _displayName(t) {
  if (!t) return "";
  return tenantDisplayName(t) || t.name || String(t.id || "");
}

function TenantCard({ title, tenant, tone }) {
  const sub = [tenant?.email, tenant?.phone]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      className={[
        "rounded-md border px-3 py-2",
        tone === "survivor"
          ? "border-emerald-500/30 bg-emerald-500/[0.06]"
          : "border-[#1e2130] bg-[#0d0f14]",
      ].join(" ")}
    >
      <div className="text-[11px] uppercase tracking-wide text-[#64748b]">
        {title}
      </div>
      <div className="truncate text-[13px] font-semibold text-[#e2e8f0]">
        {_displayName(tenant) || "—"}
      </div>
      {sub ? (
        <div className="truncate text-[11px] text-[#64748b]">{sub}</div>
      ) : null}
    </div>
  );
}

export default function TenantMergeModal({ open, survivor, onClose, onMerged }) {
  const survivorId = survivor ? String(survivor.id) : "";

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  // Reset everything whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setSelectedSource(null);
    setReason("");
    setPreview(null);
    setPreviewLoading(false);
    setExecuting(false);
    setError(null);
  }, [open]);

  // Debounced source search. Survivor is excluded client-side (do NOT rely on
  // the backend 422). Merged-away sources are already excluded by list_tenants.
  useEffect(() => {
    if (!open || selectedSource) return undefined;
    const q = String(query || "").trim();
    if (q.length < 2) {
      setResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);
    const handle = setTimeout(() => {
      fetchAdminTenants({ q, limit: 8 })
        .then((data) => {
          if (cancelled) return;
          const items = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : [];
          setResults(items.filter((x) => String(x.id) !== survivorId));
        })
        .catch(() => {
          if (cancelled) return;
          setSearchError("Suche fehlgeschlagen.");
          setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open, selectedSource, survivorId]);

  function handleSelectSource(t) {
    setSelectedSource(t);
    setQuery("");
    setResults([]);
    setPreview(null); // selecting a new source invalidates a prior preview
    setError(null);
  }

  function handleClearSource() {
    setSelectedSource(null);
    setPreview(null);
    setError(null);
  }

  async function handlePreview() {
    if (!selectedSource) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const data = await previewTenantMerge(survivorId, {
        source_tenant_id: String(selectedSource.id),
        reason: reason.trim() || null,
      });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(err?.message || "Vorschau konnte nicht geladen werden.");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleConfirm() {
    if (!selectedSource || !preview || preview.can_merge !== true) return;
    setExecuting(true);
    setError(null);
    try {
      const resp = await executeTenantMerge(survivorId, {
        source_tenant_id: String(selectedSource.id),
        reason: reason.trim() || null,
      });
      toast.success("Mieter zusammengeführt.");
      onMerged?.(resp);
      onClose?.();
    } catch (err) {
      const msg = err?.message || "Zusammenführen fehlgeschlagen.";
      setError(msg);
      toast.error(msg);
    } finally {
      setExecuting(false);
    }
  }

  function handleOpenChange(next) {
    if (!next && !executing) {
      onClose?.();
    }
  }

  const conflicts = Array.isArray(preview?.conflicts) ? preview.conflicts : [];
  const warnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
  const fills = Array.isArray(preview?.scalar_fields_to_fill)
    ? preview.scalar_fields_to_fill
    : [];
  const plannedMoves = preview?.planned_moves || {};

  const canConfirm =
    !!selectedSource &&
    !!preview &&
    preview.can_merge === true &&
    !executing &&
    !previewLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[680px]">
        <DialogHeader>
          <DialogTitle>Mit Duplikat zusammenführen</DialogTitle>
          <DialogDescription>
            Hängt alle Daten des Quell-Mieters an den aktuellen Mieter
            (Ziel) und markiert den Quell-Mieter als zusammengeführt.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-2">
            <TenantCard
              title="Ziel-Mieter (bleibt bestehen)"
              tenant={survivor}
              tone="survivor"
            />
            <TenantCard
              title="Quelle / Duplikat"
              tenant={selectedSource}
              tone="source"
            />
          </div>

          <TenantParticipantPicker
            query={query}
            onQueryChange={setQuery}
            results={results}
            loading={searchLoading}
            error={searchError}
            selectedTenant={selectedSource}
            onSelect={handleSelectSource}
            onClear={handleClearSource}
            disabled={executing}
          />

          <div>
            <label htmlFor="merge-reason" className={labelCls}>
              Grund / Notiz (optional)
            </label>
            <textarea
              id="merge-reason"
              className={fieldCls}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={executing}
              placeholder="z. B. Doppelt erfasster Mieter"
            />
          </div>

          <div>
            <button
              type="button"
              onClick={handlePreview}
              disabled={!selectedSource || previewLoading || executing}
              className="rounded-md border border-[#1e2130] bg-[#13161f] px-3 py-2 text-sm font-medium text-[#e2e8f0] disabled:opacity-50"
            >
              {previewLoading ? "Vorschau wird geladen …" : "Vorschau prüfen"}
            </button>
          </div>

          {error ? (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300">
              {error}
            </div>
          ) : null}

          {!preview && !previewLoading ? (
            <p className="m-0 text-[12px] text-[#64748b]">
              Zuerst einen Quell-Mieter wählen und „Vorschau prüfen“ klicken.
            </p>
          ) : null}

          {preview ? (
            <div className="grid gap-3 rounded-md border border-[#1e2130] bg-[#0b0d12] p-3">
              {conflicts.length > 0 ? (
                <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2">
                  <div className="mb-1 text-[12px] font-semibold text-red-300">
                    Zusammenführen nicht möglich:
                  </div>
                  <ul className="m-0 list-disc pl-5 text-[13px] text-red-200">
                    {conflicts.map((c) => (
                      <li key={c.code}>{c.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {warnings.length > 0 ? (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2">
                  <ul className="m-0 list-disc pl-5 text-[13px] text-amber-200">
                    {warnings.map((w) => (
                      <li key={w.code}>{w.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div>
                <div className={labelCls}>Wird übernommen</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-[#cbd5e1]">
                  {Object.keys(COUNT_LABELS).map((key) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-[#94a3b8]">
                        {COUNT_LABELS[key]}
                      </span>
                      <span className="font-semibold">
                        {Number(plannedMoves[key] || 0)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className={labelCls}>Leere Felder werden übernommen</div>
                {fills.length > 0 ? (
                  <p className="m-0 text-[13px] text-[#cbd5e1]">
                    Diese leeren Felder des Ziel-Mieters werden aus dem
                    Quell-Mieter übernommen:{" "}
                    <span className="font-semibold">
                      {fills
                        .map((f) => FIELD_LABELS[f] || f)
                        .join(", ")}
                    </span>
                    .
                  </p>
                ) : (
                  <p className="m-0 text-[13px] text-[#64748b]">
                    Keine Felder werden übernommen.
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={executing}
            className="rounded-md border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-sm text-[#94a3b8] disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="rounded-md border border-[#5b8cff] bg-[#5b8cff] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {executing ? "Wird zusammengeführt …" : "Zusammenführen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
